import type { ApiSchemas } from '@rental/contracts/client'
import type { PrivacyHold, StaffLocale } from '@rental/domain'
import { format, formatDate, formatDateTime, type StaffMessages } from '@rental/i18n'
import { romeNow } from '../time'

/*
 * S8: the privacy panel's facts in words. What keeps a customer's data is the
 * server's to decide (the hold reader, the job's own verdicts); the screen
 * only says it: each hold by name, with the day it ends where it has one.
 */

type Verdict = ApiSchemas['ErasurePartVerdict']
type HoldLine = ApiSchemas['PrivacyHoldLine']

/** The day an instant fell on Ischia, as a date: the panel's ends are days, not minutes. */
export function romeDay(locale: StaffLocale, instant: string): string {
  return formatDate(locale, romeNow(new Date(instant)).date, 'short')
}

/**
 * When files were erased, in words: their day, or the first and the last of
 * their days. One booking's go together, so almost always one day; a damage
 * repaired later lets its photos go later.
 */
export function erasedWhen(
  instants: readonly string[],
  locale: StaffLocale,
  m: StaffMessages,
): string {
  const days = [...new Set(instants.map((instant) => romeNow(new Date(instant)).date))].sort()
  const first = days[0] ?? ''
  const last = days.at(-1) ?? first
  return first === last
    ? format(m.inspection.erasedOnDay, { date: formatDate(locale, first, 'short') })
    : format(m.inspection.erasedBetween, {
        from: formatDate(locale, first, 'short'),
        to: formatDate(locale, last, 'short'),
      })
}

/**
 * When a hold ends. A request's 24 hours end at a minute, so the minute is
 * said; every other end is a day.
 */
function endOf(line: HoldLine, locale: StaffLocale): string | null {
  if (line.until === null) return null
  return line.hold === 'request_grace'
    ? formatDateTime(locale, line.until)
    : romeDay(locale, line.until)
}

/**
 * What keeps a part, each hold named and, where it has one, its end. Said
 * after "Trattenuti": each phrase carries its own preposition, as Italian
 * joins it to the article (dal blocco legale, dalle 24 ore).
 */
export function holdWords(
  lines: readonly HoldLine[],
  locale: StaffLocale,
  m: StaffMessages,
): string {
  return lines
    .map((line) => {
      const name = m.privacy.heldBy[line.hold]
      const end = endOf(line, locale)
      return end === null ? name : format(m.privacy.until, { hold: name, date: end })
    })
    .join(', ')
}

/** The holds that keep a booking, after "Trattenuta": a booking's holds have no end of their own. */
export function bookingHoldWords(holds: readonly PrivacyHold[], m: StaffMessages): string {
  return holds.map((hold) => m.privacy.heldBy[hold]).join(', ')
}

/** What happens to one part of the customer's data, as the hourly job will act on it. */
export function verdictWords(verdict: Verdict, locale: StaffLocale, m: StaffMessages): string {
  const copy = m.privacy.verdict
  switch (verdict.state) {
    case 'erased':
      return format(copy.erased, { date: romeDay(locale, verdict.at) })
    case 'held':
      return format(copy.held, { holds: holdWords(verdict.holds, locale, m) })
    case 'erasable':
      return copy.erasable
    case 'kept':
      return format(copy.kept, { date: romeDay(locale, verdict.until) })
    case 'not_configured':
      return copy.notConfigured
  }
}
